import { useState, useEffect } from 'react';

/**
 * Custom hook to load Google Maps API
 * @returns {boolean} mapLoaded - Whether the Google Maps API has been loaded
 * 
 * Usage:
 * const mapLoaded = useGoogleMaps();
 * 
 * if (!mapLoaded) {
 *   return <div>Loading map...</div>;
 * }
 * 
 * // Now you can use window.google.maps
 */
export const useGoogleMaps = () => {
    const [mapLoaded, setMapLoaded] = useState(false);

    useEffect(() => {
        // Check if already loaded
        if (window.google && window.google.maps) {
            setMapLoaded(true);
            return;
        }

        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
            existingScript.addEventListener('load', () => setMapLoaded(true));
            return;
        }

        // Load the script
        const script = document.createElement('script');
        const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

        if (!apiKey) {
            console.error('Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your .env file');
            return;
        }

        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => setMapLoaded(true);
        script.onerror = () => {
            console.error('Failed to load Google Maps API');
            setMapLoaded(false);
        };

        document.head.appendChild(script);
    }, []);

    return mapLoaded;
};

/**
 * Utility function to initialize a Google Map
 * @param {string} elementId - The ID of the div element to render the map
 * @param {Object} options - Map initialization options
 * @returns {google.maps.Map|null} The map instance
 */
export const initializeMap = (elementId, options = {}) => {
    const mapElement = document.getElementById(elementId);
    if (!mapElement) {
        console.error(`Element with ID "${elementId}" not found`);
        return null;
    }

    if (!window.google || !window.google.maps) {
        console.error('Google Maps API not loaded');
        return null;
    }

    const defaultOptions = {
        center: { lat: 20.5937, lng: 78.9629 }, // India center
        zoom: 5,
        mapTypeControl: false,
        ...options,
    };

    return new window.google.maps.Map(mapElement, defaultOptions);
};

/**
 * Utility function to create a marker
 * @param {google.maps.Map} map - The map instance
 * @param {Object} position - Lat/Lng position { lat, lng }
 * @param {Object} options - Marker options
 * @returns {google.maps.Marker} The marker instance
 */
export const createMarker = (map, position, options = {}) => {
    const defaultOptions = {
        position,
        map,
        draggable: true,
        ...options,
    };

    return new window.google.maps.Marker(defaultOptions);
};

/**
 * Utility function to geocode a location (get address from coordinates)
 * @param {Object} location - Lat/Lng object { lat, lng }
 * @returns {Promise<string>} The formatted address
 */
export const reverseGeocode = async (location) => {
    return new Promise((resolve, reject) => {
        if (!window.google || !window.google.maps) {
            reject(new Error('Google Maps API not loaded'));
            return;
        }

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location }, (results, status) => {
            if (status === 'OK' && results[0]) {
                resolve(results[0].formatted_address);
            } else {
                reject(new Error(`Geocoding failed: ${status}`));
            }
        });
    });
};

/**
 * Utility function to create a search box
 * @param {HTMLInputElement} inputElement - The input element for search
 * @param {google.maps.Map} map - The map instance
 * @returns {google.maps.places.SearchBox} The search box instance
 */
export const createSearchBox = (inputElement, map) => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error('Google Maps Places API not loaded');
        return null;
    }

    const searchBox = new window.google.maps.places.SearchBox(inputElement);

    // Bias results to current map's viewport
    map.addListener('bounds_changed', () => {
        searchBox.setBounds(map.getBounds());
    });

    return searchBox;
};

export default useGoogleMaps;
